/**
 * A closed-term records file, read into the typed historical import's rows
 * (ADR 0039 Intake, ADR 0012).
 *
 * The server's typed payload is the contract: each row names its kind, its
 * registrar-chosen `import_key` and its data, and the server validates every
 * row again, per row, on the dry run. This module only maps IMAM's own
 * template onto that payload. It derives no key: the registrar writes each
 * row's `key`, and a row refers to another only by the key written there, so
 * nothing is reconciled by a name, a title or an email.
 *
 * CSV columns (header row, any order, extra columns refused):
 *
 *   record      term | faculty | course | student | enrollment | outcome
 *   key         the registrar's own stable key for this row
 *   name        term name, instructor name, or the learner's legal name
 *   start_date, end_date                         term rows, YYYY-MM-DD
 *   term_key, code, title, credits,
 *   primary_faculty_key, co_instructor_faculty_keys (separated by ;)
 *                                                course rows
 *   student_id  a source SID, when the source has one
 *   email       contact evidence only, never an identity
 *   status      student: completed | active | leave_of_absence | withdrawn
 *               enrollment: complete | enrolled | withdrawn | incomplete
 *               course: archived | draft
 *   student_key, course_key                      enrollment rows
 *   enrollment_key, result_letter, result_score  outcome rows
 *
 *   reason      ignored: a refused-rows export writes each row's refusal
 *               here, so the export reads back as it was saved. It is never
 *               sent.
 *
 * An instructor row names a person as source text. Which IMAM faculty record
 * it becomes is the registrar's explicit choice in the review, never the
 * file's: the row is read with no record until that choice is made.
 *
 * ONE ROW PER ENROLLMENT. A CSV whose header has no record and key columns is
 * read as the usual registrar export instead: each line one learner in one
 * section, its columns mapped onto RECORDS_FIELDS (lib/source-columns.ts says
 * how a column is suggested; the registrar sees and can change every one).
 * The rows are derived, and the derivation is the whole of it:
 *
 *   term        key: the term name, as written
 *   section     key: term key|course code, then |section identifier when that
 *               column is mapped and the line has one
 *   instructor  key: the instructor's name as written, and each co-instructor
 *               (separated by ;). It only groups lines naming the same text;
 *               the IMAM record stays the registrar's choice per instructor.
 *   learner     key: the learner source ID, as written. REQUIRED: a learner is
 *               never keyed or merged by name or email, so a line without one
 *               is refused and so is a file without the column.
 *   enrollment  key: section key|learner key
 *   outcome     key: the enrollment's key, only where a result letter is present
 *
 * Lines repeating a parent's values collapse into one row; the same key with
 * different values is a problem naming the lines. Credits are required for a
 * section. Statuses are folded to the server's spelling ("Leave of absence" is
 * leave_of_absence). Each derived row's source_ref records the lines it came
 * from.
 */
import type { EntityKind, ImportRowInput } from "@/lib/api/imports";
import { parseCsv } from "@/lib/registrar-roster-source";
import { collapse, foldStatus, lineOf, missingFields, sentenceLabels, tableOf, valuesOf, type ColumnMap, type SourceField, type SourceTable } from "@/lib/source-columns";

export const RECORD_COLUMNS = [
  "record", "key", "name", "start_date", "end_date",
  "term_key", "code", "title", "credits", "primary_faculty_key", "co_instructor_faculty_keys",
  "student_id", "email", "status", "student_key", "course_key",
  "enrollment_key", "result_letter", "result_score",
] as const;

type RecordColumn = (typeof RECORD_COLUMNS)[number];

const KINDS: readonly EntityKind[] = ["term", "faculty", "course", "student", "enrollment", "outcome"];

/** The columns each record kind reads, besides record and key. */
export const RECORD_KIND_COLUMNS: Record<EntityKind, readonly RecordColumn[]> = {
  term: ["name", "start_date", "end_date"],
  faculty: ["name"],
  course: ["term_key", "code", "title", "credits", "primary_faculty_key", "co_instructor_faculty_keys", "status"],
  student: ["name", "student_id", "email", "status"],
  enrollment: ["student_key", "course_key", "status"],
  outcome: ["enrollment_key", "result_letter", "result_score"],
};

export interface RecordsReading {
  rows: ImportRowInput[];
  /** Lines the reader could not turn into a row, each with its line number. */
  problems: string[];
}

/** The template's example, one row of each kind, chained by key. */
export const RECORDS_TEMPLATE_ROWS: Array<Partial<Record<RecordColumn, string>>> = [
  { record: "term", key: "fall-2019", name: "Fall 2019", start_date: "2019-09-03", end_date: "2019-12-20" },
  { record: "faculty", key: "f-hassan", name: "Hassan Ali" },
  { record: "course", key: "fall-2019-eth101", term_key: "fall-2019", code: "ETH-101", title: "Introduction to Ethics", credits: "3", primary_faculty_key: "f-hassan" },
  { record: "student", key: "s-zainab", name: "Zainab Karim", email: "zainab@example.org", status: "completed" },
  { record: "enrollment", key: "e-zainab-eth101", student_key: "s-zainab", course_key: "fall-2019-eth101", status: "complete" },
  { record: "outcome", key: "o-zainab-eth101", enrollment_key: "e-zainab-eth101", result_letter: "A", result_score: "94" },
];

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

/** The downloadable template: the header and the example rows. */
export function recordsTemplateCsv(): string {
  const lines = [RECORD_COLUMNS.join(","), ...RECORDS_TEMPLATE_ROWS.map((row) => RECORD_COLUMNS.map((c) => csvCell(row[c] ?? "")).join(","))];
  return `${lines.join("\r\n")}\r\n`;
}

const DATA_FIELD: Partial<Record<EntityKind, Partial<Record<RecordColumn, string>>>> = {
  term: { name: "name" },
  student: { name: "legal_name" },
};

function rowFromFields(fields: Partial<Record<RecordColumn, string>>, line: number): ImportRowInput | string {
  const present = (name: RecordColumn) => (fields[name] ?? "").trim();
  const kind = present("record").toLowerCase() as EntityKind;
  if (!KINDS.includes(kind)) return `line ${line}: record must be one of ${KINDS.join(", ")}, not ${JSON.stringify(fields.record ?? "")}`;
  const key = present("key");
  if (!key) return `line ${line}: a ${kind} row needs its key`;
  if (key.length > 500) return `line ${line}: the key is longer than 500 characters`;
  const allowed = RECORD_KIND_COLUMNS[kind];
  const stray = RECORD_COLUMNS.filter((c) => c !== "record" && c !== "key" && !allowed.includes(c) && present(c));
  if (stray.length) return `line ${line}: a ${kind} row does not carry ${stray.join(", ")}`;
  const evidence: Record<string, unknown> = { line, record: kind };
  const data: Record<string, unknown> = {};
  for (const column of allowed) {
    const value = present(column);
    if (!value) continue;
    evidence[column] = value;
    if (kind === "faculty") continue;
    const field = DATA_FIELD[kind]?.[column] ?? column;
    if (column === "co_instructor_faculty_keys") data[field] = value.split(";").map((v) => v.trim()).filter(Boolean);
    else if ((column === "credits" && /^\d+$/.test(value)) || (column === "result_score" && /^\d+(\.\d+)?$/.test(value))) data[field] = Number(value);
    else data[field] = value;
  }
  return { entity_kind: kind, import_key: key, source_ref: evidence, data };
}

/** Read on a template line and never sent: a refused-rows export's refusal. */
const IGNORED_COLUMNS = ["reason"];

/** IMAM's own template: its header names record and key columns. */
export function isRecordsTemplate(header: readonly string[]): boolean {
  const names = header.map((cell) => cell.trim().toLowerCase());
  return names.includes("record") && names.includes("key");
}

export function readRecordsCsv(text: string): RecordsReading {
  const records = parseCsv(text);
  if (records.length === 0) return { rows: [], problems: ["the file is empty"] };
  const header = records[0].map((cell) => cell.trim().toLowerCase());
  const unknown = header.filter((name) => !(RECORD_COLUMNS as readonly string[]).includes(name) && !IGNORED_COLUMNS.includes(name));
  if (unknown.length) return { rows: [], problems: [`unknown column${unknown.length === 1 ? "" : "s"}: ${unknown.join(", ")}`] };
  if (!header.includes("record") || !header.includes("key")) return { rows: [], problems: ["the header needs record and key columns"] };
  const rows: ImportRowInput[] = [];
  const problems: string[] = [];
  records.slice(1).forEach((record, offset) => {
    const fields: Partial<Record<RecordColumn, string>> = {};
    header.forEach((name, column) => { fields[name as RecordColumn] = record[column] ?? ""; });
    const row = rowFromFields(fields, offset + 2);
    if (typeof row === "string") problems.push(row); else rows.push(row);
  });
  return { rows, problems };
}

/** The server's own typed rows, verbatim: a list, or an object with a rows list. */
export function readRecordsJson(text: string): RecordsReading {
  let parsed: unknown;
  try { parsed = JSON.parse(text); } catch { return { rows: [], problems: ["the file is not valid JSON"] }; }
  const list = Array.isArray(parsed) ? parsed
    : typeof parsed === "object" && parsed !== null && Array.isArray((parsed as { rows?: unknown }).rows) ? (parsed as { rows: unknown[] }).rows : null;
  if (!list) return { rows: [], problems: ["the file is neither a list of rows nor an object with a rows list"] };
  const rows: ImportRowInput[] = [];
  const problems: string[] = [];
  list.forEach((item, index) => {
    const candidate = (typeof item === "object" && item !== null ? item : {}) as Partial<ImportRowInput>;
    if (!KINDS.includes(candidate.entity_kind as EntityKind)) { problems.push(`row ${index + 1}: entity_kind must be one of ${KINDS.join(", ")}`); return; }
    if (typeof candidate.import_key !== "string" || !candidate.import_key.trim()) { problems.push(`row ${index + 1}: import_key is required`); return; }
    rows.push({ entity_kind: candidate.entity_kind!, import_key: candidate.import_key, source_ref: candidate.source_ref ?? { row: index + 1 }, data: candidate.data ?? {} });
  });
  return { rows, problems };
}

export function readRecordsFile(name: string, text: string): RecordsReading {
  const lower = name.toLowerCase();
  if (lower.endsWith(".csv")) return readRecordsCsv(text);
  if (lower.endsWith(".json")) return readRecordsJson(text);
  return { rows: [], problems: [`${name} is neither a .csv nor a .json file`] };
}

/** A records file as read: the template's rows, or an export's table waiting for its columns. */
export interface RecordsSource extends RecordsReading { table?: SourceTable }

export function readRecordsSource(name: string, text: string): RecordsSource {
  if (!name.toLowerCase().endsWith(".csv")) return readRecordsFile(name, text);
  const records = parseCsv(text);
  if (records.length === 0) return { rows: [], problems: ["the file is empty"] };
  if (isRecordsTemplate(records[0])) return readRecordsCsv(text);
  return { rows: [], problems: [], table: tableOf(records) };
}

export type RecordsField = "term_name" | "term_start" | "term_end" | "course_code" | "course_title" | "credits" | "section"
  | "instructor_name" | "co_instructors" | "learner_source_id" | "learner_name" | "email" | "learner_status" | "enrollment_status"
  | "result_letter" | "result_score";

/** The fields a one-row-per-enrollment export fills, in reading order, with the only aliases that prefill them. */
export const RECORDS_FIELDS: readonly SourceField<RecordsField>[] = [
  { id: "term_name", label: "Term name", required: true, aliases: ["term", "semester"] },
  { id: "term_start", label: "Term start", required: false, aliases: ["start date", "term start date"] },
  { id: "term_end", label: "Term end", required: false, aliases: ["end date", "term end date"] },
  { id: "course_code", label: "Course code", required: true, aliases: ["code", "course", "course number"] },
  { id: "course_title", label: "Course title", required: true, aliases: ["title", "course name"] },
  { id: "credits", label: "Credits", required: true, aliases: ["credit", "credit hours", "units"] },
  { id: "section", label: "Section identifier", required: false, aliases: ["section", "section id", "section number"] },
  { id: "instructor_name", label: "Instructor name", required: false, aliases: ["instructor", "faculty", "teacher"] },
  { id: "co_instructors", label: "Co-instructors", required: false, aliases: ["co instructor"] },
  { id: "learner_source_id", label: "Learner source ID", required: true, aliases: ["sid", "student id", "student number", "learner id"] },
  { id: "learner_name", label: "Learner name", required: true, aliases: ["student name", "legal name", "full name"] },
  { id: "email", label: "Email", required: false, aliases: ["e-mail", "email address", "student email"] },
  { id: "learner_status", label: "Learner status", required: false, aliases: ["student status"] },
  { id: "enrollment_status", label: "Enrollment status", required: false, aliases: ["registration status"] },
  { id: "result_letter", label: "Result letter", required: false, aliases: ["grade", "letter grade", "final grade"] },
  { id: "result_score", label: "Score", required: false, aliases: ["result score", "final score", "percentage"] },
];

const LABELS = sentenceLabels(RECORDS_FIELDS);

const whole = (value: string) => /^\d+$/.test(value) ? Number(value) : value;
const score = (value: string) => /^\d+(\.\d+)?$/.test(value) ? Number(value) : value;
/** Only the values a line has: a blank field is left for the server's default. */
const filled = (data: Record<string, unknown>) => Object.fromEntries(Object.entries(data).filter(([, v]) => v !== "" && !(Array.isArray(v) && !v.length)));

/**
 * A one-row-per-enrollment export's rows, derived as the module header says.
 * The map must give every required field a column; `missingFields` names the ones it does not.
 */
export function deriveRecords(table: SourceTable, map: ColumnMap): RecordsReading {
  const terms = collapse("term", LABELS), sections = collapse("section", LABELS), instructors = collapse("instructor", LABELS);
  const learners = collapse("learner", LABELS), enrollments = collapse("enrollment", LABELS);
  const sectionOf = new Map<string, { term: string; code: string; section: string }>();
  const enrollmentOf = new Map<string, { section: string; learner: string }>();
  const problems: string[] = [];
  table.records.forEach((record, offset) => {
    const line = lineOf(offset);
    const v = valuesOf(RECORDS_FIELDS, map, record);
    if (!v.learner_source_id) { problems.push(`line ${line}: no learner source ID. Add the learner's ID from your system; a learner is never matched by name or email.`); return; }
    const blank = RECORDS_FIELDS.filter((f) => f.required && !v[f.id]).map((f) => LABELS[f.id]);
    if (blank.length) { problems.push(`line ${line}: no ${blank.join(", ")}`); return; }
    if (v.result_score && !v.result_letter) { problems.push(`line ${line}: a score without a result letter`); return; }
    const co = v.co_instructors.split(";").map((name) => name.trim()).filter(Boolean);
    const section = [v.term_name, v.course_code, ...(v.section ? [v.section] : [])].join("|");
    const enrollment = `${section}|${v.learner_source_id}`;
    sectionOf.set(section, { term: v.term_name, code: v.course_code, section: v.section });
    enrollmentOf.set(enrollment, { section, learner: v.learner_source_id });
    terms.add(v.term_name, { term_start: v.term_start, term_end: v.term_end }, line);
    sections.add(section, { course_title: v.course_title, credits: v.credits, instructor_name: v.instructor_name, co_instructors: co.join("; ") }, line);
    for (const name of [v.instructor_name, ...co].filter(Boolean)) instructors.add(name, {}, line);
    learners.add(v.learner_source_id, { learner_name: v.learner_name, email: v.email, learner_status: v.learner_status }, line);
    enrollments.add(enrollment, { enrollment_status: v.enrollment_status, result_letter: v.result_letter, result_score: v.result_score }, line);
  });
  const rows: ImportRowInput[] = [
    ...terms.entries().map(({ key, values, lines }) => ({ entity_kind: "term" as const, import_key: key, source_ref: { record: "term", lines },
      data: filled({ name: key, start_date: values.term_start, end_date: values.term_end }) })),
    ...instructors.entries().map(({ key, lines }) => ({ entity_kind: "faculty" as const, import_key: key, source_ref: { record: "faculty", lines, name: key }, data: {} })),
    ...sections.entries().map(({ key, values, lines }) => {
      const s = sectionOf.get(key)!;
      return { entity_kind: "course" as const, import_key: key, source_ref: filled({ record: "course", lines, section: s.section }),
        data: filled({ term_key: s.term, code: s.code, title: values.course_title, credits: whole(values.credits),
          primary_faculty_key: values.instructor_name, co_instructor_faculty_keys: values.co_instructors ? values.co_instructors.split("; ") : [] }) };
    }),
    ...learners.entries().map(({ key, values, lines }) => ({ entity_kind: "student" as const, import_key: key, source_ref: { record: "student", lines, name: values.learner_name },
      data: filled({ student_id: key, legal_name: values.learner_name, email: values.email, status: values.learner_status && foldStatus(values.learner_status) }) })),
    ...enrollments.entries().flatMap(({ key, values, lines }) => {
      const k = enrollmentOf.get(key)!;
      const enrollment = { entity_kind: "enrollment" as const, import_key: key, source_ref: { record: "enrollment", lines },
        data: filled({ student_key: k.learner, course_key: k.section, status: values.enrollment_status && foldStatus(values.enrollment_status) }) };
      return values.result_letter ? [enrollment, { entity_kind: "outcome" as const, import_key: key, source_ref: { record: "outcome", lines },
        data: filled({ enrollment_key: key, result_letter: values.result_letter, result_score: values.result_score && score(values.result_score) }) }] : [enrollment];
    }),
  ];
  return { rows, problems: [...problems, ...terms.problems(), ...sections.problems(), ...learners.problems(), ...enrollments.problems()] };
}

/** An export's rows under a map: none until every required field has a column. */
export function readMappedRecords(table: SourceTable, map: ColumnMap): RecordsReading {
  return missingFields(RECORDS_FIELDS, map).length ? { rows: [], problems: [] } : deriveRecords(table, map);
}

/** The export example: two learners in one section, so the collapse is visible. */
export function enrollmentExportTemplateCsv(): string {
  const lines = [
    RECORDS_FIELDS.map((f) => f.label),
    ["Fall 2019", "2019-09-03", "2019-12-20", "ETH-101", "Introduction to Ethics", "3", "", "Hassan Ali", "", "S-1001", "Zainab Karim", "zainab@example.org", "completed", "complete", "A", "94"],
    ["Fall 2019", "2019-09-03", "2019-12-20", "ETH-101", "Introduction to Ethics", "3", "", "Hassan Ali", "", "S-1002", "Yusuf Haddad", "", "completed", "complete", "B+", ""],
  ];
  return `${lines.map((line) => line.map(csvCell).join(",")).join("\r\n")}\r\n`;
}

/** An instructor row a file named as source text: its IMAM record waits for the registrar's choice. */
export function facultyNeedsChoice(row: ImportRowInput): boolean {
  return row.entity_kind === "faculty" && row.source_ref?.record === "faculty"
    && (typeof row.source_ref?.line === "number" || Array.isArray(row.source_ref?.lines));
}

/** The instructor choices already made, carried onto rows read again from the same file. */
export function carryFacultyChoices(from: readonly ImportRowInput[], to: ImportRowInput[]): ImportRowInput[] {
  const chosen = new Map(from.filter(facultyNeedsChoice).map((r) => [r.import_key, facultyChoiceOf(r)]));
  return to.map((r) => facultyNeedsChoice(r) && chosen.get(r.import_key) ? withFacultyChoice(r, chosen.get(r.import_key)!) : r);
}

export function facultySourceName(row: ImportRowInput): string {
  return String(row.source_ref?.name ?? row.import_key);
}

export type FacultyChoice = { action: "create" } | { action: "link"; faculty_profile_id: string } | null;

/** The choice an instructor row's data records, read back for the review. */
export function facultyChoiceOf(row: ImportRowInput): FacultyChoice {
  const data = row.data ?? {};
  if (typeof data.faculty_profile_id === "string") return { action: "link", faculty_profile_id: data.faculty_profile_id };
  if (typeof data.display_name === "string") return { action: "create" };
  return null;
}

/** An instructor row with the registrar's choice written into its data, as the server reads it. */
export function withFacultyChoice(row: ImportRowInput, choice: FacultyChoice): ImportRowInput {
  if (!choice) return { ...row, data: {} };
  if (choice.action === "link") return { ...row, data: { faculty_profile_id: choice.faculty_profile_id } };
  return { ...row, data: { display_name: facultySourceName(row), account_link_state: "not_linked", portal_access: "none" } };
}

/** A file's rows, counted the way the registrar reads them. */
export function summariseRecords(rows: ImportRowInput[]): Record<EntityKind, number> {
  const counts = Object.fromEntries(KINDS.map((k) => [k, 0])) as Record<EntityKind, number>;
  for (const row of rows) counts[row.entity_kind] += 1;
  return counts;
}
